import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  environment,
  MyHttpClientFactory,
  ResponseCasingEnum,
} from '@ups/xplat/core';
import { map, Observable, of, switchMap, take } from 'rxjs';
import { VpGlAccount } from '../models/vp-gl-account.model';
import {
  GL_ACCOUNT_SEARCH_TOP,
  toVpGlAccount,
  VpGlAccountRecord,
} from '../utils/gl-account-search.utils';
import { vendorGroupForCompany } from '../utils/vendor-search.utils';

/** WI 34966: Viewpoint GL account search for the `typeahead-gl-account` dynamic control. */
@Injectable({
  providedIn: 'root',
})
export class SearchVpGlAccountService {
  private readonly http: HttpClient;

  constructor(clientFactory: MyHttpClientFactory) {
    this.http = clientFactory.createHttpClient(
      environment.urls.viewpointAPI,
      true,
      ResponseCasingEnum.CamelCase
    );
  }

  /**
   * Accounts of the GL company `vpCompanyId` posts to, matching `query`. No usable company returns
   * an empty list, never another company's chart.
   */
  search(
    query: string,
    vpCompanyId: number | null | undefined
  ): Observable<Array<VpGlAccount>> {
    // A usable Viewpoint company number is the same 1..255 tinyint check the vendor group uses.
    const company = vendorGroupForCompany(vpCompanyId);
    if (company == null) {
      return of([]);
    }
    let params = new HttpParams()
      .set('vpCompanyId', company)
      .set('top', GL_ACCOUNT_SEARCH_TOP);
    const term = `${query ?? ''}`.trim();
    if (term) {
      params = params.set('search', term);
    }
    return this.http
      .get<Array<VpGlAccountRecord>>('/api/vpservice/accounts/search', {
        params,
      })
      .pipe(map((records) => (records ?? []).map(toVpGlAccount)));
  }

  /**
   * `search`, once `company$` first emits. A form's reopen prefill can search before the user's
   * profile loads; waiting keeps that search from failing closed and dropping the saved account.
   */
  searchForCompany(
    query: string,
    company$: Observable<number | null | undefined>
  ): Observable<Array<VpGlAccount>> {
    return company$.pipe(
      take(1),
      switchMap((company) => this.search(query, company))
    );
  }
}
